import * as d3 from 'd3';
import { Theme } from '@mui/material';
import { MarketMetadata, MarketAnalysisData, SeriesVisibility, VolumeVisibility, AggregatedVolumePoint } from '../../types';
import { ChartScales, ChartLayout, ParsedChartPoint } from '../types';
import {
    findNearbyTrade2D,
    buildFullSeriesTooltipLines,
    renderFullSeriesTooltip,
    renderSimpleMarketTooltip,
    getCandlePeriodMs,
} from '../../tooltipHelpers';

interface TooltipElements {
    tooltip: d3.Selection<SVGGElement, unknown, null, undefined>;
    hoverLine: d3.Selection<SVGLineElement, unknown, null, undefined>;
}

interface TooltipConfig {
    scales: ChartScales;
    layout: ChartLayout;
    theme: Theme;
    useDecimalScale: boolean;
}

interface TooltipDataContext {
    parsedData: ParsedChartPoint[];
    analysisData: MarketAnalysisData | null;
    aggregatedVolume: AggregatedVolumePoint[];
    markets: MarketMetadata[];
    isFullSeriesMode: boolean;
    seriesVisibility: SeriesVisibility | undefined;
    volumeVisibility: VolumeVisibility;
}

export function createTooltipElements(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    layout: ChartLayout,
    theme: Theme
): TooltipElements {
    const tooltip = g.append('g')
        .attr('class', 'tooltip')
        .style('display', 'none')
        .style('pointer-events', 'none');

    tooltip.append('rect')
        .attr('fill', 'rgba(0, 0, 0, 0.8)')
        .attr('rx', 4)
        .attr('ry', 4);

    tooltip.append('text')
        .attr('fill', 'white')
        .attr('font-size', '12px')
        .attr('text-anchor', 'start');

    const hoverLine = g.append('line')
        .attr('class', 'hover-line')
        .attr('y1', 0)
        .attr('y2', layout.totalChartHeight)
        .attr('stroke', theme.palette.divider)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3 3')
        .style('display', 'none')
        .style('pointer-events', 'none');

    return { tooltip, hoverLine };
}

export function createTooltipHandlers(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    elements: TooltipElements,
    config: TooltipConfig,
    dataContext: TooltipDataContext
): {
    handleMouseMove: (this: Element, event: MouseEvent) => void;
    handleMouseOut: () => void;
} {
    const { tooltip, hoverLine } = elements;
    const { scales, layout, useDecimalScale } = config;
    const {
        parsedData,
        analysisData,
        aggregatedVolume,
        markets,
        isFullSeriesMode,
        seriesVisibility,
        volumeVisibility
    } = dataContext;

    const xThresholdPx = layout.innerWidth * 0.015;
    const yThresholdPx = layout.priceHeight * 0.015;
    let highlightedTradeId: string | null = null;

    function handleMouseMove(this: Element, event: MouseEvent) {
        const [mouseX, mouseY] = d3.pointer(event, this);
        const date = scales.x.invert(mouseX);

        hoverLine.attr('x1', mouseX).attr('x2', mouseX).style('display', null);

        const textElement = tooltip.select('text') as d3.Selection<SVGTextElement, unknown, null, undefined>;

        if (isFullSeriesMode && analysisData && seriesVisibility) {
            const nearbyTrade = findNearbyTrade2D(mouseX, mouseY, analysisData.trades, scales.x, scales.y, xThresholdPx, yThresholdPx);
            const candlePeriodMs = getCandlePeriodMs(analysisData.candles || []);
            const { lines: fullSeriesLines } = buildFullSeriesTooltipLines(
                date, analysisData, seriesVisibility, volumeVisibility,
                aggregatedVolume, useDecimalScale, nearbyTrade, candlePeriodMs
            );

            // Update trade marker highlighting
            const newHighlightId = nearbyTrade ? `trade-marker-${nearbyTrade.transactionId}` : null;
            if (highlightedTradeId && highlightedTradeId !== newHighlightId) {
                const prevMarker = g.select(`#${highlightedTradeId}`);
                if (!prevMarker.empty()) {
                    const baseSize = parseFloat(prevMarker.attr('data-base-size') || '48');
                    prevMarker.transition().duration(150)
                        .attr('d', d3.symbol().type(d3.symbolTriangle).size(baseSize)());
                }
            }
            if (newHighlightId && newHighlightId !== highlightedTradeId) {
                const newMarker = g.select(`#${newHighlightId}`);
                if (!newMarker.empty()) {
                    const baseSize = parseFloat(newMarker.attr('data-base-size') || '48');
                    newMarker.transition().duration(150)
                        .attr('d', d3.symbol().type(d3.symbolTriangle).size(baseSize * 3)());
                }
            }
            highlightedTradeId = newHighlightId;

            const { maxWidth } = renderFullSeriesTooltip(textElement, fullSeriesLines);
            const bbox = (textElement.node() as SVGTextElement).getBBox();
            tooltip.select('rect')
                .attr('width', Math.max(bbox.width, maxWidth) + 16)
                .attr('height', bbox.height + 8);

            const tooltipX = Math.min(mouseX + 10, layout.innerWidth - Math.max(bbox.width, maxWidth) - 20);
            const tooltipY = Math.max(10, Math.min(event.offsetY - bbox.height - 20, layout.priceHeight - bbox.height - 16));
            tooltip.attr('transform', `translate(${tooltipX}, ${tooltipY})`).style('display', null);
            return;
        }

        // Simple market mode tooltip
        if (parsedData.length === 0) return;

        let closestPoint = parsedData[0];
        let minDistance = Math.abs(closestPoint.parsedTimestamp.getTime() - date.getTime());
        parsedData.forEach(point => {
            const distance = Math.abs(point.parsedTimestamp.getTime() - date.getTime());
            if (distance < minDistance) {
                minDistance = distance;
                closestPoint = point;
            }
        });

        const tooltipLines = [`Date: ${closestPoint.parsedTimestamp.toLocaleDateString('de-DE')}`, ''];
        markets.forEach((market) => {
            let value = closestPoint[`market_${market.id}`];
            if (value === undefined || isNaN(value)) {
                let closestMarketPoint = null;
                let minMarketDistance = Infinity;
                parsedData.forEach(point => {
                    const marketValue = point[`market_${market.id}`];
                    if (marketValue !== undefined && !isNaN(marketValue)) {
                        const distance = Math.abs(point.parsedTimestamp.getTime() - closestPoint.parsedTimestamp.getTime());
                        if (distance < minMarketDistance) {
                            minMarketDistance = distance;
                            closestMarketPoint = point;
                        }
                    }
                });
                if (closestMarketPoint) value = closestMarketPoint[`market_${market.id}`];
            }
            const formattedValue = (value !== undefined && !isNaN(value))
                ? (useDecimalScale ? value.toFixed(2) : `${value.toFixed(2)}%`)
                : 'N/A';
            tooltipLines.push(`${market.name}: ${formattedValue}`);
        });

        renderSimpleMarketTooltip(textElement, tooltipLines);
        const bbox = (textElement.node() as SVGTextElement).getBBox();
        tooltip.select('rect').attr('width', bbox.width + 12).attr('height', bbox.height + 8);

        const tooltipX = Math.min(mouseX + 10, layout.innerWidth - bbox.width - 16);
        const tooltipY = Math.max(10, Math.min(event.offsetY - bbox.height - 20, layout.priceHeight - bbox.height - 16));
        tooltip.attr('transform', `translate(${tooltipX}, ${tooltipY})`).style('display', null);
    }

    function handleMouseOut() {
        tooltip.style('display', 'none');
        hoverLine.style('display', 'none');
        if (highlightedTradeId) {
            const prevMarker = g.select(`#${highlightedTradeId}`);
            if (!prevMarker.empty()) {
                const baseSize = parseFloat(prevMarker.attr('data-base-size') || '48');
                prevMarker.transition().duration(150)
                    .attr('d', d3.symbol().type(d3.symbolTriangle).size(baseSize)());
            }
            highlightedTradeId = null;
        }
    }

    return { handleMouseMove, handleMouseOut };
}
